import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or get session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Analytics tracker hook
export const useAnalytics = () => {
  const location = useLocation();
  const pageStartTime = useRef(Date.now());
  const currentPage = useRef(location.pathname);
  const sessionId = getSessionId();

  // Track page view and time spent
  const trackPageView = useCallback(async (page, timeSpent = 0) => {
    try {
      await axios.post(`${API_URL}/api/analytics/pageview`, {
        session_id: sessionId,
        page,
        time_spent: timeSpent,
        user_agent: navigator.userAgent,
        referrer: document.referrer || '',
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      // Silent fail - don't interrupt user experience
      console.debug('Analytics pageview error:', error);
    }
  }, [sessionId]);

  // Track click events
  const trackClick = useCallback(async (element, page = location.pathname) => {
    try {
      await axios.post(`${API_URL}/api/analytics/click`, {
        session_id: sessionId,
        element,
        page,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.debug('Analytics click error:', error);
    }
  }, [sessionId, location.pathname]);

  // Track downloads
  const trackDownload = useCallback(async (fileName, fileType, page = location.pathname) => {
    try {
      await axios.post(`${API_URL}/api/analytics/download`, {
        session_id: sessionId,
        file_name: fileName,
        file_type: fileType,
        page,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.debug('Analytics download error:', error);
    }
  }, [sessionId, location.pathname]);

  // Track blog reading time
  const trackBlogTime = useCallback(async (slug, timeSpent) => {
    try {
      await axios.post(`${API_URL}/api/analytics/blog-time`, {
        session_id: sessionId,
        slug,
        time_spent: timeSpent,
      });
    } catch (error) {
      console.debug('Analytics blog time error:', error);
    }
  }, [sessionId]);

  // Track page changes
  useEffect(() => {
    // Send time spent on previous page
    const timeSpent = Math.round((Date.now() - pageStartTime.current) / 1000);
    if (currentPage.current !== location.pathname && timeSpent > 0) {
      trackPageView(currentPage.current, timeSpent);
    }

    // Reset for new page
    pageStartTime.current = Date.now();
    currentPage.current = location.pathname;

    // Track new page view
    trackPageView(location.pathname, 0);

    // Track time on page unload
    const handleUnload = () => {
      const finalTime = Math.round((Date.now() - pageStartTime.current) / 1000);
      // Use sendBeacon for reliable tracking on page close
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          `${API_URL}/api/analytics/pageview`,
          JSON.stringify({
            session_id: sessionId,
            page: location.pathname,
            time_spent: finalTime,
            timestamp: new Date().toISOString(),
          })
        );
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [location.pathname, trackPageView, sessionId]);

  return {
    trackClick,
    trackDownload,
    trackBlogTime,
    sessionId,
  };
};

// Analytics Provider component
const AnalyticsTracker = ({ children }) => {
  useAnalytics();
  return children;
};

export default AnalyticsTracker;
